import { Quote } from "lucide-react";

const Head = ({ eyebrow, title, note }) => (
  <div className="mb-6">
    <p className="eyebrow">{eyebrow}</p>
    <h2 className="mt-2 font-display text-[22px] font-light leading-snug" style={{ color: "var(--text-main)" }}>{title}</h2>
    {note && <p className="mt-2 text-[12px] leading-relaxed" style={{ color: "var(--text-faint)" }}>{note}</p>}
  </div>
);

// The living memory rail: what Griot is holding right now — its sources,
// the thread of the conversation, and the ways back in.
export const MemoryRail = ({ turns = [], sources, showTranscript, showPassages = true, openings = [], ask, onOpen, state, bottom, accent = "#C87D55" }) => {
  const lastGriot = [...turns].reverse().find((t) => t.role === "griot");
  const cited = showPassages ? (sources || lastGriot?.sources || []) : [];
  const started = turns.length > 0;

  return (
    <aside
      className="relative z-10 hidden h-screen w-[33%] max-w-[430px] shrink-0 flex-col overflow-hidden border-l lg:flex"
      style={{ borderColor: "var(--border-linen)", background: "linear-gradient(180deg, rgba(20,20,18,0.85), rgba(12,12,11,0.9))" }}
      data-testid={cited.length > 0 ? "source-rail" : "memory-rail"}
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-9 py-9">
        {!started ? (
          <div className="rise rise-3">
            <Head eyebrow="ways in" title="You can begin anywhere." note="Or ask me something of your own — I'll only answer from this Lab's own record." />
            <ul data-testid="openings">
              {openings.map((o, i) => (
                <li key={o}>
                  <button
                    data-testid={`opening-${i + 1}`}
                    onClick={() => ask(o)}
                    className="group flex w-full items-start gap-3 border-t py-4 text-left transition-colors"
                    style={{ borderColor: "var(--border-linen)" }}
                  >
                    <span className="mt-[5px] font-mono text-[9.5px]" style={{ color: accent }}>{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display text-[18px] leading-snug transition-colors group-hover:text-[#F4EFE6]" style={{ color: "var(--text-dim)" }}>{o}</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-8 border-t pt-5 text-[12px] leading-relaxed" style={{ borderColor: "var(--border-linen)", color: "var(--text-faint)" }}>
              Everything I say carries its source. If a passage isn't shown here, it wasn't used.
            </p>
          </div>
        ) : (
          <>
            {cited.length > 0 && (
              <div className="ember-in">
                <Head eyebrow="where this came from" title="The passages I leaned on." />
                <ul className="space-y-3">
                  {cited.map((s, i) => (
                    <li key={s.id || i}>
                      <button
                        data-testid={`source-rail-item-${i + 1}`}
                        onClick={() => onOpen?.(s)}
                        className="group w-full rounded-2xl p-4 text-left transition-colors hover:bg-white/[0.035]"
                        style={{ border: "1px solid var(--border-linen)", borderLeft: `2px solid ${accent}99`, background: "rgba(244,239,230,0.02)" }}
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="font-mono text-[9.5px]" style={{ color: accent }}>[{s.n}]</span>
                          <span className="min-w-0 flex-1 truncate font-display text-[16px]" style={{ color: "var(--text-main)" }}>{s.title}</span>
                        </div>
                        <p className="mt-2 line-clamp-4 font-display text-[14px] italic leading-relaxed" style={{ color: "var(--text-dim)" }}>“{s.excerpt}”</p>
                        <div className="mt-3 flex items-center justify-between font-mono text-[9.5px] uppercase tracking-[0.18em]" style={{ color: "var(--text-faint)" }}>
                          <span>{s.type}{s.date ? ` · ${s.date}` : ""}</span>
                          <span className="opacity-0 transition-opacity group-hover:opacity-100" style={{ color: accent }}>read it</span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {showTranscript && (
              <div className={cited.length > 0 ? "mt-10 border-t pt-8" : ""} style={{ borderColor: "var(--border-linen)" }} data-testid="desktop-conversation">
                <Head eyebrow="the thread" title="What we've said so far." />
                <div className="space-y-6">
                  {turns.map((t, i) =>
                    t.role === "user" ? (
                      <p key={i} className="ml-auto max-w-[88%] rounded-[18px] rounded-br-sm px-4 py-2.5 text-[13px] leading-relaxed" style={{ background: "rgba(244,239,230,0.055)", color: "var(--text-dim)" }}>{t.text}</p>
                    ) : (
                      <div key={i} className="space-y-2 border-l pl-4" style={{ borderColor: `${accent}55` }}>
                        <p className="font-display text-[17px] font-light leading-[1.55]" data-testid="griot-response" style={{ color: "var(--text-main)" }}>{t.text}</p>
                        {showPassages && t.sources?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {t.sources.map((src, j) => (
                              <button
                                key={src.id || j}
                                data-testid={`source-chip-${j + 1}`}
                                onClick={() => onOpen?.(src)}
                                className="rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] transition-colors hover:text-[#F4EFE6]"
                                style={{ borderColor: "var(--border-linen)", color: "var(--text-faint)" }}
                              >
                                [{src.n}] {src.title}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  )}
                  {state === "reflecting" && (
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em]" style={{ color: "var(--text-faint)" }}>
                      <Quote size={11} /> gathering the thread…
                    </p>
                  )}
                  <div ref={bottom} />
                </div>
              </div>
            )}

            {!showTranscript && cited.length === 0 && (
              <div className="ember-in">
                <Head eyebrow="listening" title="I'm holding what you said." note="Nothing is written to the screen unless this Lab asks for it — listen, and I'll show you the sources as they come." />
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
};

export const SourceRail = MemoryRail;
